import React, { useState, useEffect, useRef } from 'react';
import { useUser, useAuth } from '@clerk/clerk-react';
import {
  LayoutDashboard,
  Users,
  Search,
  Plus,
  Menu,
  X,
  ChevronRight,
  Activity,
  Zap,
  TrendingUp,
  AlertCircle,
  ShieldCheck,
  Upload,
  LogOut,
  Settings,
} from 'lucide-react';
import { CustomerDetail } from './components/CustomerDetail';
import { AuthPage } from './components/AuthPage';
import { AccessPending } from './components/AccessPending';
import { AdminPanel } from './components/admin/AdminPanel';
import { usePermissions } from './contexts/PermissionContext';
import { customersApi, dashboardApi, settingsApi } from './lib/supabase';
import type { Customer } from './types';
import { Button, Card, Modal, LoadingSpinner, Input } from './components/UIComponents';

const DEFAULT_LOGO = '/logo.png';

const BrandLogo = ({
  className = 'h-10 w-10',
  customLogo,
}: {
  className?: string;
  customLogo?: string | null;
}) => (
  <img
    src={customLogo || DEFAULT_LOGO}
    alt="Get Secured"
    className={`${className} object-contain`}
    onError={(e) => {
      (e.target as HTMLImageElement).src = DEFAULT_LOGO;
    }}
  />
);

function App() {
  const { isLoaded, isSignedIn, user } = useUser();
  const { signOut } = useAuth();
  const { permissions, portalUser, loading: permissionsLoading } = usePermissions();

  const [currentView, setCurrentView] = useState<'dashboard' | 'detail' | 'admin'>('dashboard');
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isAddCustomerOpen, setIsAddCustomerOpen] = useState(false);
  const [newCustomerName, setNewCustomerName] = useState('');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [customLogo, setCustomLogo] = useState<string | null>(null);
  const [stats, setStats] = useState({
    totalCustomers: 0,
    totalDevices: 0,
    mrr: 0,
    overdueInvoices: 0,
    activeProducts: 0,
  });

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load data when authenticated
  useEffect(() => {
    if (isSignedIn && permissions) {
      loadData();
      loadSettings();
    }
  }, [isSignedIn, permissions]);

  const loadData = async () => {
    try {
      const [customersData, statsData] = await Promise.all([
        customersApi.getAll(),
        dashboardApi.getStats(),
      ]);
      
      // Filter customers by permissions if not superuser
      const filteredCustomers = permissions?.isSuperuser
        ? customersData
        : customersData.filter((c) => permissions?.hasAccessToCustomer(c.id));
      
      setCustomers(filteredCustomers);
      setStats(statsData);
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const loadSettings = async () => {
    try {
      const logo = await settingsApi.get('brand_logo');
      setCustomLogo(logo);
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64String = reader.result as string;
        try {
          await settingsApi.set('brand_logo', base64String);
          setCustomLogo(base64String);
        } catch (error) {
          console.error('Error saving logo:', error);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const resetLogo = async () => {
    if (confirm('Reset to default branding?')) {
      try {
        await settingsApi.set('brand_logo', null);
        setCustomLogo(null);
      } catch (error) {
        console.error('Error resetting logo:', error);
      }
    }
  };

  const handleCustomerSelect = (customer: Customer) => {
    setSelectedCustomer(customer);
    setCurrentView('detail');
    setIsSidebarOpen(false);
  };

  const handleAddCustomer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCustomerName.trim()) return;

    try {
      const newCust = await customersApi.create({
        name: newCustomerName,
        status: 'active',
        address: null,
        abn: null,
        primary_contact_name: null,
        primary_contact_email: null,
        primary_contact_phone: null,
        notes: null,
      });
      setNewCustomerName('');
      setIsAddCustomerOpen(false);
      await loadData();
      handleCustomerSelect(newCust);
    } catch (error) {
      console.error('Error creating customer:', error);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      setCustomers([]);
      setSelectedCustomer(null);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Show loading state while Clerk loads
  if (!isLoaded || permissionsLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-slate-50">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Show auth page if not signed in
  if (!isSignedIn) {
    return <AuthPage />;
  }

  // Show access pending if user has no roles or access
  if (portalUser && !permissions?.highestRole) {
    return <AccessPending />;
  }

  const Dashboard = () => {
    const filteredCustomers = customers.filter(
      (c) =>
        c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.primary_contact_name?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
      <div className="space-y-8 animate-fade-in">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-black text-slate-900 tracking-tight">
              Customer Overview
            </h1>
            <p className="text-slate-500 mt-1">Managed Services Fleet Intelligence</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="bg-emerald-50 text-emerald-700 px-4 py-2 rounded-lg border border-emerald-100 flex items-center gap-2">
              <TrendingUp size={18} />
              <span className="font-bold tracking-tight">
                ${stats.mrr.toLocaleString()} MRR
              </span>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="bg-white border-l-4 border-l-accent shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-slate-400 text-[10px] font-bold uppercase tracking-widest">
                  Active Accounts
                </p>
                <p className="text-3xl font-black text-slate-900 mt-1">
                  {stats.totalCustomers}
                </p>
              </div>
              <div className="p-2 bg-blue-50 rounded-lg text-accent">
                <Users size={20} />
              </div>
            </div>
          </Card>
          <Card className="bg-white border-l-4 border-l-emerald-500 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-slate-400 text-[10px] font-bold uppercase tracking-widest">
                  Secured Seats
                </p>
                <p className="text-3xl font-black text-slate-900 mt-1">
                  {stats.activeProducts}
                </p>
              </div>
              <div className="p-2 bg-emerald-50 rounded-lg text-emerald-600">
                <Zap size={20} />
              </div>
            </div>
          </Card>
          <Card className="bg-white border-l-4 border-l-purple-500 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-slate-400 text-[10px] font-bold uppercase tracking-widest">
                  Global Fleet
                </p>
                <p className="text-3xl font-black text-slate-900 mt-1">
                  {stats.totalDevices}
                </p>
              </div>
              <div className="p-2 bg-purple-50 rounded-lg text-purple-600">
                <Activity size={20} />
              </div>
            </div>
          </Card>
          <Card
            className={`bg-white border-l-4 shadow-sm hover:shadow-md transition-shadow ${
              stats.overdueInvoices > 0 ? 'border-l-red-500' : 'border-l-amber-500'
            }`}
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="text-slate-400 text-[10px] font-bold uppercase tracking-widest">
                  Arrears Risks
                </p>
                <p
                  className={`text-3xl font-black mt-1 ${
                    stats.overdueInvoices > 0 ? 'text-red-600' : 'text-slate-900'
                  }`}
                >
                  {stats.overdueInvoices}
                </p>
              </div>
              <div
                className={`p-2 rounded-lg ${
                  stats.overdueInvoices > 0
                    ? 'bg-red-50 text-red-600'
                    : 'bg-amber-50 text-amber-600'
                }`}
              >
                <AlertCircle size={20} />
              </div>
            </div>
          </Card>
        </div>

        {/* Customers Table */}
        <Card className="overflow-hidden">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
            <h2 className="text-lg font-bold text-slate-900">Managed Accounts</h2>
            <div className="flex items-center gap-3 w-full sm:w-auto">
              <div className="relative flex-1 sm:flex-none">
                <Search
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                  size={18}
                />
                <input
                  type="text"
                  placeholder="Search fleet..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full sm:w-64 pl-10 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent/50"
                />
              </div>
              <Button onClick={() => setIsAddCustomerOpen(true)}>
                <Plus size={18} />
                <span className="hidden sm:inline">New Account</span>
              </Button>
            </div>
          </div>

          <div className="overflow-x-auto -mx-6">
            <table className="w-full min-w-[600px]">
              <thead className="bg-slate-50 border-y border-slate-100">
                <tr>
                  <th className="p-5 text-left text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                    Organization
                  </th>
                  <th className="p-5 text-left text-[10px] font-bold text-slate-400 uppercase tracking-widest hidden sm:table-cell">
                    Lead Contact
                  </th>
                  <th className="p-5 text-left text-[10px] font-bold text-slate-400 uppercase tracking-widest hidden md:table-cell">
                    Headquarters
                  </th>
                  <th className="p-5"></th>
                </tr>
              </thead>
              <tbody>
                {filteredCustomers.map((c) => (
                  <tr
                    key={c.id}
                    onClick={() => handleCustomerSelect(c)}
                    className="border-b border-slate-100 hover:bg-blue-50/50 cursor-pointer transition-colors group"
                  >
                    <td className="p-5">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-slate-100 text-slate-500 rounded-xl flex items-center justify-center font-bold text-lg group-hover:bg-accent group-hover:text-white transition-colors">
                          {c.name.charAt(0)}
                        </div>
                        <span className="font-bold text-slate-900">{c.name}</span>
                      </div>
                    </td>
                    <td className="p-5 hidden sm:table-cell text-sm text-slate-600 font-medium">
                      {c.primary_contact_name || '-'}
                    </td>
                    <td className="p-5 hidden md:table-cell text-xs text-slate-500 truncate max-w-[240px]">
                      {c.address || '-'}
                    </td>
                    <td className="p-5 text-right">
                      <div className="flex items-center justify-end text-slate-300 group-hover:text-accent transition-colors">
                        <ChevronRight size={20} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredCustomers.length === 0 && (
              <div className="p-12 text-center text-slate-400">
                {searchQuery
                  ? 'No customers match your search.'
                  : 'No customers yet. Add your first customer to get started.'}
              </div>
            )}
          </div>
        </Card>
      </div>
    );
  };

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden selection:bg-accent selection:text-white">
      {isSidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-slate-900/60 backdrop-blur-sm md:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
        fixed inset-y-0 left-0 z-50 w-72 bg-slate-950 text-white flex flex-col transform transition-transform duration-300 ease-in-out md:static md:translate-x-0
        ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}
      >
        <div className="p-8">
          <div className="group relative flex items-center gap-4">
            <div className="relative">
              <BrandLogo className="h-12 w-12 drop-shadow-2xl" customLogo={customLogo} />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="absolute -bottom-1 -right-1 p-1 bg-accent rounded-full border border-slate-900 shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
                title="Change Logo"
              >
                <Upload size={10} className="text-white" />
              </button>
            </div>
            <div className="flex flex-col">
              <span className="leading-none text-white font-black tracking-tighter">
                GET SECURED
              </span>
              <span className="text-accent text-[10px] tracking-[0.3em] font-black mt-1 uppercase">
                IT Services
              </span>
            </div>

            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              accept="image/*"
              onChange={handleLogoUpload}
            />
          </div>
          {customLogo && (
            <button
              onClick={resetLogo}
              className="mt-2 text-[9px] text-slate-600 hover:text-slate-400 font-bold uppercase tracking-widest flex items-center gap-1 transition-colors"
            >
              <X size={10} /> Reset Branding
            </button>
          )}
        </div>

        <nav className="flex-1 px-4 space-y-1">
          <button
            onClick={() => {
              setCurrentView('dashboard');
              setSelectedCustomer(null);
              setIsSidebarOpen(false);
            }}
            className={`w-full flex items-center gap-4 px-4 py-4 rounded-xl transition-all duration-200 ${
              currentView === 'dashboard' && !selectedCustomer
                ? 'bg-accent/10 text-accent border border-accent/20'
                : 'text-slate-400 hover:bg-slate-900 hover:text-white'
            }`}
          >
            <LayoutDashboard size={20} />
            <span className="font-bold text-sm">Command Center</span>
          </button>
          
          {permissions?.isSuperuser && (
            <button
              onClick={() => {
                setCurrentView('admin');
                setSelectedCustomer(null);
                setIsSidebarOpen(false);
              }}
              className={`w-full flex items-center gap-4 px-4 py-4 rounded-xl transition-all duration-200 ${
                currentView === 'admin'
                  ? 'bg-purple-500/10 text-purple-400 border border-purple-500/20'
                  : 'text-slate-400 hover:bg-slate-900 hover:text-white'
              }`}
            >
              <Settings size={20} />
              <span className="font-bold text-sm">Administration</span>
            </button>
          )}
          
          <div className="pt-8 pb-4 px-6 text-[10px] font-black text-slate-500 uppercase tracking-[0.2em]">
            Active Customers
          </div>
          <div className="space-y-1 overflow-y-auto max-h-[50vh] custom-scrollbar px-1">
            {customers.map((c) => (
              <button
                key={c.id}
                onClick={() => handleCustomerSelect(c)}
                className={`w-full flex items-center gap-4 px-4 py-3 rounded-xl transition-all duration-200 group
                  ${
                    selectedCustomer?.id === c.id
                      ? 'bg-slate-900 text-white border border-slate-800'
                      : 'text-slate-400 hover:bg-slate-900 hover:text-white'
                  }
                `}
              >
                <div
                  className={`w-1.5 h-1.5 rounded-full ${
                    selectedCustomer?.id === c.id
                      ? 'bg-accent shadow-[0_0_8px_rgba(59,130,246,0.6)]'
                      : 'bg-slate-700'
                  }`}
                />
                <span className="text-xs font-semibold truncate flex-1 text-left">{c.name}</span>
              </button>
            ))}
          </div>
        </nav>
        <div className="p-8 border-t border-slate-900">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-slate-900 rounded-xl border border-slate-800 flex items-center justify-center">
                <ShieldCheck size={18} className="text-accent" />
              </div>
              <div>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest leading-none">
                  {user?.firstName || user?.primaryEmailAddress?.emailAddress?.split('@')[0] || 'User'}
                </p>
                <p className="text-[10px] text-emerald-500 font-bold mt-1">Online</p>
              </div>
            </div>
            <button
              onClick={handleSignOut}
              className="p-2 text-slate-500 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
              title="Sign Out"
            >
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content Area */}
      <main className="flex-1 flex flex-col h-full overflow-hidden relative">
        <header className="h-16 border-b border-slate-200 bg-white px-6 flex items-center justify-between md:justify-end shrink-0 sticky top-0 z-10">
          <button
            className="md:hidden p-2 text-slate-600 hover:bg-slate-50 rounded-lg"
            onClick={() => setIsSidebarOpen(true)}
          >
            <Menu size={24} />
          </button>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-slate-400 text-xs font-bold">
              <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
              Fleet Connected
            </div>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto custom-scrollbar">
          <div className="max-w-7xl mx-auto p-6 md:p-12">
            {currentView === 'dashboard' && <Dashboard />}
            {currentView === 'admin' && (
              <AdminPanel
                onBack={() => {
                  setCurrentView('dashboard');
                  loadData();
                }}
              />
            )}
            {currentView === 'detail' && selectedCustomer && (
              <CustomerDetail
                customerId={selectedCustomer.id}
                onBack={() => {
                  setCurrentView('dashboard');
                  setSelectedCustomer(null);
                }}
                onUpdate={loadData}
              />
            )}
          </div>
        </div>
      </main>

      {/* Modal for New Customer */}
      <Modal
        isOpen={isAddCustomerOpen}
        onClose={() => setIsAddCustomerOpen(false)}
        title="Initialize New Environment"
      >
        <form onSubmit={handleAddCustomer} className="space-y-6">
          <div className="p-6 bg-slate-50 rounded-2xl border border-slate-100">
            <Input
              label="Legal Business Name"
              value={newCustomerName}
              onChange={(e) => setNewCustomerName(e.target.value)}
              required
              autoFocus
              placeholder="e.g. Wayne Enterprises"
              className="bg-white"
            />
            <p className="text-xs text-slate-500 mt-4 leading-relaxed italic">
              Provisioning a new account will create a record for managing hardware, licences, and
              billing.
            </p>
          </div>
          <Button type="submit" className="w-full py-6 text-lg rounded-2xl">
            Provision Instance
          </Button>
        </form>
      </Modal>
    </div>
  );
}

export default App;
